import type { Paragraph } from '@/api/types/stowed-element'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import type { GetVariantFn } from '../types/font-lookup'
import {
  collectAllFontIds,
  getFirstResolvedFontFamilyId,
  isMixedByStyleName,
  isResolvedFontFamilyMixed,
} from './font-family-selection'
import { getFirstRunProperty, isRunPropertyMixed } from './run-properties'

/**
 * What the Font Family button and Style select should show for a selection. Only the fields that
 * are present get applied; an absent field leaves that part of the display as it was.
 * `ensureFamilyId` names a family whose faces should start loading because it is now shown.
 */
export interface FontFamilyDisplay {
  familyIdValue?: string
  familyIdMixed?: boolean
  mixedSelectionFontIds?: number[]
  selectionFontIdsAll?: number[]
  variantFontIdValue?: string
  variantFontIdMixed?: boolean
  variantStyleNamePlaceholder?: string
  ensureFamilyId?: number
}

/** Live text editing: read from the editor's own selection style. */
export function resolveLiveEditFamilyDisplay(
  style: TextRunStyleUpdate | null | undefined,
  getVariant: GetVariantFn
): FontFamilyDisplay {
  const display: FontFamilyDisplay = { selectionFontIdsAll: style?.selectionFontIds ?? [] }

  if (style?.mixedFontFamilyId) {
    display.familyIdMixed = true
    display.familyIdValue = ''
    display.mixedSelectionFontIds = style.selectionFontIds ?? []
  } else if (typeof style?.fontFamilyId === 'number') {
    display.familyIdMixed = false
    display.familyIdValue = String(style.fontFamilyId)
    display.ensureFamilyId = style.fontFamilyId
    display.mixedSelectionFontIds = []
  }

  if (style?.mixedFontFamilyId) {
    // Families differ: compare style names so "Regular + Regular" isn't treated as mixed
    const selectionFontIds = style.selectionFontIds ?? []
    const styleMixed = isMixedByStyleName(selectionFontIds, getVariant)

    display.variantFontIdMixed = styleMixed
    display.variantFontIdValue = ''
    display.variantStyleNamePlaceholder = styleMixed
      ? ''
      : (getVariant(selectionFontIds[0])?.fontStyleName ?? '')
  } else if (style?.mixedFontId) {
    display.variantFontIdMixed = true
    display.variantFontIdValue = ''
    display.variantStyleNamePlaceholder = ''
  } else if (typeof style?.fontId === 'number') {
    display.variantFontIdMixed = false
    display.variantFontIdValue = String(style.fontId)
    display.variantStyleNamePlaceholder = ''
  }

  return display
}

/** Nothing selected: show the book/checkpoint default family, no variant. */
export function resolveEmptyFamilyDisplay(
  defaultFontFamilyId: number | undefined
): FontFamilyDisplay {
  return {
    familyIdValue: defaultFontFamilyId != null ? String(defaultFontFamilyId) : '',
    ensureFamilyId: defaultFontFamilyId,
    familyIdMixed: false,
    mixedSelectionFontIds: [],
    selectionFontIdsAll: [],
    variantFontIdMixed: false,
    variantFontIdValue: '',
    variantStyleNamePlaceholder: '',
  }
}

/** Select-Tool whole-box selection: read every run of the selected paragraphs. */
export function resolveParagraphFamilyDisplay(
  paragraphs: Paragraph[],
  getVariant: GetVariantFn
): FontFamilyDisplay {
  const ff = getFirstResolvedFontFamilyId(paragraphs, getVariant)
  const ffMixed = isResolvedFontFamilyMixed(paragraphs, getVariant)
  const allFontIds = collectAllFontIds(paragraphs)
  const showFamily = !ffMixed && typeof ff === 'number'
  const display: FontFamilyDisplay = {
    selectionFontIdsAll: allFontIds,
    familyIdMixed: ffMixed,
    familyIdValue: showFamily ? String(ff) : '',
    ensureFamilyId: showFamily ? ff : undefined,
  }

  if (ffMixed) {
    // Families differ: compare style names so "Regular + Regular" isn't treated as mixed
    const fidMixed = isMixedByStyleName(allFontIds, getVariant)

    display.mixedSelectionFontIds = allFontIds
    display.variantFontIdMixed = fidMixed
    display.variantFontIdValue = ''
    display.variantStyleNamePlaceholder = fidMixed
      ? ''
      : (getVariant(allFontIds[0])?.fontStyleName ?? '')
  } else {
    const fid = getFirstRunProperty(paragraphs, 'fontId')
    const fidMixed = isRunPropertyMixed(paragraphs, 'fontId')

    display.mixedSelectionFontIds = []
    display.variantFontIdMixed = fidMixed
    display.variantStyleNamePlaceholder = ''
    display.variantFontIdValue = !fidMixed && typeof fid === 'number' ? String(fid) : ''
  }

  return display
}
